import React, { useState } from 'react';
import { Box, Tab, Tabs, TextField } from '@mui/material';
import { Locale } from '@services/workout.service';

const LOCALES: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

const FLAG_EMOJIS: Record<Locale, string> = {
  uk: '🇺🇦',
  en: '🇬🇧',
  ru: '🇷🇺',
  cs: '🇨🇿',
  es: '🇪🇸',
  pl: '🇵🇱',
  be: '🇧🇾',
};

interface LocalizedInputProps {
  value: Partial<Record<Locale, string>>;
  onChange: (value: Partial<Record<Locale, string>>) => void;
  label: string;
  multiline?: boolean;
  rows?: number;
  requiredLocale?: Locale;
  // Контрольована активна вкладка — щоб кілька LocalizedInput на одній формі
  // (напр. заголовок + текст) перемикали мову СИНХРОННО. Без цього кожен
  // інстанс мав власний незалежний activeTab (useState всередині) — перемикання
  // мови для заголовка ніяк не впливало на вкладку тексту, і навпаки: людина
  // перемикала одне поле на іншу мову, а друге лишалось на попередній — легко
  // переплутати й переписати чужий переклад. Якщо не передано — компонент
  // працює як раніше, з власним внутрішнім станом (для одиночного використання).
  activeTab?: number;
  onTabChange?: (index: number) => void;
}

export const LocalizedInput: React.FC<LocalizedInputProps> = ({
  value,
  onChange,
  label,
  multiline = false,
  rows = 4,
  requiredLocale = 'uk',
  activeTab: controlledActiveTab,
  onTabChange,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
  const setActiveTab = isControlled ? (onTabChange ?? (() => {})) : setInternalActiveTab;

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}
      >
        {LOCALES.map((lang) => {
          const isEmpty = !value[lang];
          return (
            <Tab
              key={lang}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{FLAG_EMOJIS[lang]}</span>
                  <span>{lang.toUpperCase()}</span>
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isEmpty
                        ? lang === requiredLocale ? 'error.main' : 'warning.main'
                        : 'success.main',
                      display: 'inline-block',
                      ml: 0.25,
                    }}
                  />
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {LOCALES.map((lang, index) => (
        <Box key={lang} hidden={activeTab !== index}>
          <TextField
            label={`${label} (${lang.toUpperCase()})`}
            fullWidth
            multiline={multiline}
            rows={multiline ? rows : undefined}
            required={lang === requiredLocale}
            value={value[lang] || ''}
            onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
          />
        </Box>
      ))}
    </Box>
  );
};
